import React, { useEffect, useRef } from 'react';

export const Fireworks: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;

    const resizeCanvas = () => {
      const parent = canvas.parentElement;
      if (parent) {
        canvas.width = parent.clientWidth;
        canvas.height = parent.clientHeight;
      } else {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
      }
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    class Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      alpha: number;
      decay: number;
      color: string;
      gravity: number;
      friction: number;
      size: number;

      constructor(x: number, y: number, color: string) {
        this.x = x;
        this.y = y;
        const angle = Math.random() * Math.PI * 2;
        const speed = Math.random() * 4 + 1.5;
        this.vx = Math.cos(angle) * speed;
        this.vy = Math.sin(angle) * speed;
        this.alpha = 1;
        this.decay = Math.random() * 0.015 + 0.008;
        this.color = color;
        this.gravity = 0.05;
        this.friction = 0.97;
        this.size = Math.random() * 1.5 + 1.5;
      }

      update() {
        this.vx *= this.friction;
        this.vy *= this.friction;
        this.vy += this.gravity;
        this.x += this.vx;
        this.y += this.vy;
        this.alpha -= this.decay;
      }

      draw(c: CanvasRenderingContext2D) {
        c.save();
        c.globalAlpha = this.alpha;
        c.beginPath();
        c.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        c.fillStyle = this.color;
        c.shadowBlur = 4;
        c.shadowColor = this.color;
        c.fill();
        c.restore();
      }
    }

    class Shell {
      x: number;
      y: number;
      tx: number;
      ty: number;
      vx: number;
      vy: number;
      color: string;
      exploded: boolean;

      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = canvas.height;
        this.tx = Math.random() * canvas.width * 0.8 + canvas.width * 0.1;
        this.ty = Math.random() * canvas.height * 0.4 + canvas.height * 0.1;
        const angle = Math.atan2(this.ty - this.y, this.tx - this.x);
        const speed = Math.random() * 3 + 6;
        this.vx = Math.cos(angle) * speed;
        this.vy = Math.sin(angle) * speed;

        const hues = [0, 40, 80, 120, 170, 200, 240, 280, 320];
        const randomHue = hues[Math.floor(Math.random() * hues.length)];
        this.color = `hsl(${randomHue}, 100%, 60%)`;
        this.exploded = false;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;

        if (this.vy >= 0 || this.y <= this.ty) {
          this.exploded = true;
        }
      }

      draw(c: CanvasRenderingContext2D) {
        c.beginPath();
        c.arc(this.x, this.y, 2, 0, Math.PI * 2);
        c.fillStyle = this.color;
        c.fill();
      }
    }

    let particles: Particle[] = [];
    let shells: Shell[] = [];
    let ticks = 0;

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      ticks++;
      if (ticks % 50 === 0 && shells.length < 4) {
        shells.push(new Shell());
      }

      shells = shells.filter((shell) => {
        shell.update();
        if (shell.exploded) {
          for (let i = 0; i < 35; i++) {
            particles.push(new Particle(shell.x, shell.y, shell.color));
          }
          return false;
        }
        shell.draw(ctx);
        return true;
      });

      particles = particles.filter((p) => {
        p.update();
        if (p.alpha <= 0) return false;
        p.draw(ctx);
        return true;
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    const timerId = setTimeout(() => {
      shells.push(new Shell());
      animate();
    }, 450);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      clearTimeout(timerId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    />
  );
};
